import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Grid, H6, Paper } from '@dhl-ui/components'
import { type GridColDef, Pagination } from '@dhl/components'
import { useGetUsers, useUpdateOrganisationForUser, type UserDto } from '@dhl/mdp-api'

import { AdminDataGrid, renderUserName, SearchTextField, useNotificationMessage, useSearchAndPage } from '~/common'
import { getErrorMessage } from '~/common/util/axiosUtil'

import { convertToUser, getUserTypeLabel } from '~/modules/users/model'

type Props = {
  organisationId: string
  onClose?: () => void
  onRefreshData: (value: boolean) => void
}

export const ExistingUsers = ({ organisationId, onClose, onRefreshData }: Props) => {
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const { mutateAsync: updateOrganisationForUser } = useUpdateOrganisationForUser()
  const { searchAndPageParams, handleSearch, page, pageSize, setPage, setPageSize, search } = useSearchAndPage()
  const { data, isFetching } = useGetUsers(
    {
      ...searchAndPageParams,
    },
    {
      query: {
        select: ({ content = [], totalCount }) => ({ content: content.map(convertToUser), totalCount }),
      },
    }
  )

  const users = data?.content || []

  const addExistingUsers = async () => {
    const selectedUsers = users.filter((user: { id: string }) => selectedIds.includes(user.id))
    await Promise.allSettled(selectedUsers.map((user) => updateUser(user.email)))
  }

  const updateUser = useCallback(
    async (emailOrId: string) => {
      try {
        await updateOrganisationForUser({ emailOrId, organisationId })
        showSuccess(t('admin.organisationsPage.users.addUserMessage'))
        onRefreshData(true)
        if (onClose) {
          onClose()
        }
      } catch (error) {
        const message = getErrorMessage(error, t('admin.organisationsPage.users.errorMessage'))
        showError(message)
      }
    },
    [organisationId, t, onRefreshData, onClose]
  )

  const renderRightsCell = (row: UserDto) => {
    return row.isBusinessUser ? getUserTypeLabel('business') : getUserTypeLabel('consumer')
  }

  const renderCompanyCell = (row: UserDto) => row?.name.companyName

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('admin.organisationsPage.users.name'),
      flex: 1,
      valueGetter: (value, row) => renderUserName(row),
    },
    {
      field: 'isBusinessUser',
      headerName: t('admin.organisationsPage.users.rights'),
      flex: 1,
      valueGetter: (value, row) => renderRightsCell(row),
    },
    {
      field: 'companyName',
      headerName: t('admin.organisationsPage.users.company'),
      flex: 1,
      valueGetter: (value, row) => renderCompanyCell(row),
    },
  ]

  return (
    <Box pt={2}>
      <SearchTextField onChange={handleSearch} />
      <Grid container sx={{ mt: 1 }}>
        <Box sx={{ ml: 1, py: 1 }}>
          <H6>
            {t('admin.selected')}: {selectedIds.length}
          </H6>
        </Box>
        {selectedIds.length > 0 && (
          <Button variant="text" onClick={() => setSelectedIds([])}>
            {t('admin.clear')}
          </Button>
        )}
      </Grid>
      <Box mt={0}>
        <Paper>
          <AdminDataGrid
            loading={isFetching}
            data-testid="existing-users-table"
            autoHeight
            keepNonExistentRowsSelected
            rows={users}
            columns={columns}
            rowHeight={48}
            columnHeaderHeight={48}
            checkboxSelection={true}
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(newSelectionModel) => {
              setSelectedIds(newSelectionModel as string[])
            }}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
        </Paper>
        <Box mt={2}>
          <Pagination
            page={page}
            count={data?.totalCount || 0}
            onPageChange={(_, page) => setPage(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={setPageSize}
            hideTotal
          />
        </Box>
      </Box>
      <Box marginTop={2} marginBottom={2} display="flex" justifyContent="flex-end">
        <Box marginRight={1}>
          <Button color="primary" onClick={onClose}>
            {t('admin.organisationsPage.users.cancel')}
          </Button>
        </Box>
        <Button size="medium" color="primary" type="submit" variant="contained" onClick={() => addExistingUsers()}>
          {t('admin.organisationsPage.users.addUserButton')}
        </Button>
      </Box>
    </Box>
  )
}
